import {useEffect, useRef, useState} from "react";

import {useLevelOrder} from "@hooks/LevelOrder";
import {
  clearCallDashboard,
  getLastActivity,
} from "@redux/slices/dashboardSlices/dashboardCallSlice";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {Spinner} from "@ui/general/Spinner";
import * as FlagIcons from "country-flag-icons/react/3x2";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import {Head} from "../../components/Head";
import styles from "./styles.module.scss";

export const Activity = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {currentLevelStyle, currentLevel} = useLevelOrder();

  const {data, status, error} = useSelector(
    (state) => state.dashboard.call.lastActivity,
  );

  const [opacityBottomBtn, setOpacityBottomBtn] = useState(1);
  const [opacityTopBtn, setOpacityTopBtn] = useState(0);

  const listRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (listRef.current) {
        const {scrollTop, scrollHeight, clientHeight} = listRef.current;

        const isAtBottom = scrollTop + clientHeight >= scrollHeight * 0.95;
        const isAtTop = scrollTop <= scrollHeight * 0.05;

        setOpacityBottomBtn(isAtBottom ? 0 : 1);
        setOpacityTopBtn(isAtTop ? scrollTop / (scrollHeight * 0.05) : 1);
      }
    };

    const listElement = listRef.current;

    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (listElement) {
        listElement.removeEventListener("scroll", handleScroll);
      }

      dispatch(clearCallDashboard());
    };
  }, []);

  const filteredSessions =
    data &&
    data.filter((session) => {
      const createdAt = moment(session.created_at);

      return createdAt.isAfter(moment().subtract(24, "hours"));
    });

  const scroll = (direction) => {
    if (listRef.current) {
      const scrollAmount = listRef.current.clientHeight;

      listRef.current.scrollBy({
        top: direction * scrollAmount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    dispatch(getLastActivity());
  }, [dispatch]);

  const renderCountryFlag = (countryCode) => {
    if (!countryCode || typeof countryCode !== "string") {
      return null;
    }

    const normalizedCountryCode = countryCode.toUpperCase();
    const FlagIcon = FlagIcons[normalizedCountryCode];

    if (!FlagIcon) {
      return (
        <span className={styles.countryCodeFallback}>{normalizedCountryCode}</span>
      );
    }

    return (
      <FlagIcon
        className={styles.countryFlag}
        title={normalizedCountryCode}
      />
    );
  };

  return (
    <div className={styles.activity}>
      <Head
        title={
          <span style={{display: "inline-block", width: "min-content"}}>
            {t("page.dashboard.activity_title")}
          </span>
        }
        updateCharts={() => dispatch(getLastActivity())}
        maxWidth={"100%"}
      />

      {status === "loading" && (
        <Spinner
          width={50}
          height={50}
        />
      )}

      <div style={{position: "relative"}}>
        {listRef.current?.scrollHeight !== listRef.current?.clientHeight && (
          <div
            className={styles.scrollTopBtn}
            style={{opacity: opacityTopBtn}}
            onClick={() => scroll(-1)}
          >
            <i />
          </div>
        )}

        <ul
          ref={listRef}
          className={styles.userList}
        >
          {status === "success" &&
            (!filteredSessions || filteredSessions.length === 0) && (
              <li className={styles.error}>
                <SmallDesc>
                  <span
                    className={"text-[var(--grey)]"}
                    dangerouslySetInnerHTML={{
                      __html: t("page.dashboard.activity_empty"),
                    }}
                  />
                </SmallDesc>
              </li>
            )}

          {status === "error" && (
            <li className={styles.error}>
              <SmallDesc>
                <span className={"text-[var(--red)]"}>{error}</span>
              </SmallDesc>
            </li>
          )}

          {filteredSessions &&
            filteredSessions.length > 0 &&
            filteredSessions.map((session) => (
              <li key={session?.id}>
                <div className={styles.itemHead}>
                  <img
                    src={session?.user_cover}
                    alt={"avatar"}
                  />

                  <RootDesc>
                    <span className={styles.userLabel}>
                      {renderCountryFlag(session?.user_country_code)}
                      {session?.user}
                    </span>
                  </RootDesc>
                </div>

                <div className={styles.itemFooter}>
                  <div className={styles.itemFooterContent}>
                    <SmallDesc>
                      <span className={"text-[var(--grey)]"}>
                        {moment(session?.created_at).fromNow()}
                      </span>
                    </SmallDesc>

                    <RootDesc>
                      <span>
                        {session?.type === "ScenarioSession"
                          ? session?.title || session?.scenario
                          : session?.title || session?.product}
                      </span>
                    </RootDesc>
                  </div>

                  <div
                    style={currentLevelStyle(session?.level)}
                    className={styles.itemLevel}
                    title={currentLevel(session?.level)}
                  >
                    {currentLevel(session?.level)}
                  </div>
                </div>
              </li>
            ))}
        </ul>

        {listRef.current?.scrollHeight !== listRef.current?.clientHeight && (
          <div
            className={styles.scrollDownBtn}
            style={{opacity: opacityBottomBtn}}
            onClick={() => scroll(1)}
          >
            <i />
          </div>
        )}
      </div>
    </div>
  );
};
